import React, { useState } from 'react';
import { getCountries, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js';

// Country picker + phone input. Normalizes to E.164 via libphonenumber-js against
// the selected country, so members typing with or without a dial code — US (+1),
// Nigerian (+234), Haitian (+509), etc. — all resolve to a clean, unambiguous
// number. Reports { text, country, e164, isValid, isEmpty } via onChange; e164 is
// '' unless the number is valid for the selected country.

// Flag emoji from an ISO country code (regional indicator letters).
const flagEmoji = (cc) =>
  cc.toUpperCase().replace(/./g, (ch) => String.fromCodePoint(127397 + ch.charCodeAt(0)));

let regionNames = null;
try { regionNames = new Intl.DisplayNames(['en'], { type: 'region' }); } catch { regionNames = null; }

// Built once at module load.
const COUNTRY_OPTIONS = getCountries()
  .map((cc) => ({ cc, name: (regionNames && regionNames.of(cc)) || cc, code: getCountryCallingCode(cc) }))
  .sort((a, b) => a.name.localeCompare(b.name));

const PhoneField = ({
  id,
  defaultCountry = 'US',
  onChange,
  error = false,
  disabled = false,
  placeholder = 'Phone number',
}) => {
  const [country, setCountry] = useState(defaultCountry);
  const [text, setText] = useState('');

  const emit = (nextText, nextCountry) => {
    const parsed = parsePhoneNumberFromString(nextText || '', nextCountry);
    const isValid = !!(parsed && parsed.isValid());
    if (onChange) {
      onChange({
        text: nextText,
        country: nextCountry,
        e164: isValid ? parsed.number : '',
        isValid,
        isEmpty: !nextText.trim(),
      });
    }
  };

  return (
    <div className="flex gap-2">
      <select
        aria-label="Country code"
        value={country}
        disabled={disabled}
        onChange={(e) => { setCountry(e.target.value); emit(text, e.target.value); }}
        className="w-36 sm:w-40 shrink-0 px-2 py-2 border border-neutral-300 rounded-md bg-neutral-100 text-neutral-900 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
      >
        {COUNTRY_OPTIONS.map((c) => (
          <option key={c.cc} value={c.cc}>
            {flagEmoji(c.cc)} {c.name} +{c.code}
          </option>
        ))}
      </select>
      <input
        id={id}
        type="tel"
        value={text}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => { setText(e.target.value); emit(e.target.value, country); }}
        className={`flex-1 min-w-0 px-3 py-2 border rounded-md text-neutral-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
          error ? 'border-error-500' : 'border-neutral-300'
        }`}
      />
    </div>
  );
};

export default PhoneField;
